import { Button } from '@gisce/oficina-virtual-components';
import { Refresh } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FC } from 'react';
import authActions from "../Authentication/actions";
import { useDispatch } from 'react-redux';
import ErrorLayout from './components/ErrorLayout';

type ErrorCodes<T extends number> = T | (number & {});


/* Error code types
  500 -> Generic anything could've happened
  503 -> Website is in maintenance
  504 -> Openid is active, code and state was provided but nonce was not found
*/
type HandledErrorCodes = ErrorCodes<500 | 503 | 504 | 505>

export type ErrorComponentPageProps = {
  status: HandledErrorCodes;
};

const ErrorComponentPage: FC<ErrorComponentPageProps> = (error) => { 
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const getImageSrcFromErrorCode = (statusCode: number): string | undefined  => {
    switch (statusCode) {
      case 500: return "/images/error/crash/lightpole.jpg";
      case 503: return "/images/maintenance.jpg";
      default: return "/images/error/crash/lightpole.jpg";
    }
  }

  const getErrorTitleFromCode = (statusCode: number): string => {
    switch (statusCode) {
      case 500: return t('common:text.error.crash_view_title');
      case 503: return t('common:text.maintenance.view_title');
      case 504: return t('common:text.error.nonce_not_present_title');
      case 505: return t('common:text.error.clientid_incorrect_title');
      default: return t('common:text.error.page_title');
    }
  }

  const handleContinue = () => {
    dispatch(authActions.clearError());
  }

  return <ErrorLayout
    imageSrc={getImageSrcFromErrorCode(error.status)}
    errorTitle={getErrorTitleFromCode(error.status)}
    errorDetailComponent={<>
      {error.status === 503 &&
        <Box
          component="img"
          display="flex"
          alignSelf="center"
          sx={{
            [theme.breakpoints.down("md")]: {
              marginTop: 3,
              marginBottom: 3,
            },
            [theme.breakpoints.up("md")]: {
              marginBottom: 6
            },
            width: "75%"
          }}
          src="/images/logo.png"
        />
      }
      <Typography variant="h6" pb={1}>
        {error.status && t('common:text.error.code_message', {"errorCode": error.status})}
      </Typography>
      <Typography variant="body1">
        {error.status === 500 && t('common:text.error.crash_view_message')}
        {error.status === 503 && t('common:text.maintenance.view_subtitle')}
        {error.status === 504 && t('common:text.error.nonce_not_present_explanation')}
        {error.status === 505 && t('common:text.error.clientid_incorrect_explanation')}
      </Typography>
      {error.status !== 503 &&
        <Typography variant="body1">
          {t('common:text.error.link_text')}
        </Typography>
      }
    </>}
    redirectComponent={
      <Stack
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
        sx={{marginTop: 3}}
        spacing={2}
      >
        {[504, 505].includes(error.status) &&
          <Button 
            variant="contained" 
            color="primary"
            startIcon={
              <Refresh />
            }
            onClick={() => {navigate("/")}}
          >
          {t('common:text.error.refresh_button')}
        </Button>}

        {[500, 503].includes(error.status) &&
          <Button 
            variant="contained" 
            color="primary"
            startIcon={
              <Refresh />
            }
            onClick={() => navigate(0)}
          >
          {t('common:text.error.refresh_button')}
        </Button>}
      </Stack>
    }
  />
}

export default ErrorComponentPage;
